import { useState, useEffect, useCallback } from "react";
import { useLang } from "../lib/LangContext";

/* =================================================================
 *  Kreux Architecture — Lead Capture Modal
 *  Triggered exclusively by the Hero "Experience The Intelligence" CTA.
 *  Inherits palette + typography from #matrix-operations-layer tokens
 *  and Hero tokens; all visual rules scoped under .karch-modal.
 * ================================================================= */

const WEBHOOK_URL =
  "https://script.google.com/macros/s/AKfycbwGotvXswW1z0w0gj2Yzsdeag9_SLw-NkzdsCstJ4qSk4j6lxT-D2ev_jbd-faUf3g0/exec";

const ArchitectureModal = ({ open, onClose }) => {
  const { t, lang } = useLang();

  const [form, setForm] = useState({
    name: "", company: "", email: "", category: "", phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState({});
  const [gdprAccepted, setGdprAccepted] = useState(false);

  /* Reset on open */
  useEffect(() => {
    if (open) {
      setForm({ name: "", company: "", email: "", category: "", phone: "" });
      setErr({});
      setSuccess(false);
      setSubmitting(false);
      setGdprAccepted(false);
      try { document.body.style.overflow = "hidden"; } catch (_) { /* noop */ }
      return () => { try { document.body.style.overflow = ""; } catch (_) { /* noop */ } };
    }
  }, [open]);

  /* ESC closes when not submitting */
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("arch.err.required");
    if (!form.company.trim()) e.company = t("arch.err.required");
    if (!form.category.trim()) e.category = t("arch.err.required");
    const email = form.email.trim().toLowerCase();
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!re.test(email)) e.email = t("arch.err.email");
    if (!form.phone.trim() || !/^\+?\d[\d\s-]{4,}$/.test(form.phone.trim()))
      e.phone = t("arch.err.phone");
    setErr(e);
    return Object.keys(e).length === 0;
  };

  const submit = useCallback(async (ev) => {
    ev.preventDefault();
    if (submitting || success) return;
    if (!validate()) return;
    setSubmitting(true);

    /* POST JSON payload. Apps Script web apps reject application/json
     * cross-origin preflight, so we send the stringified JSON body with
     * text/plain — Apps Script reads e.postData.contents and parses it. */
    const body = JSON.stringify({
      name: form.name.trim(),
      company: form.company.trim(),
      email: form.email.trim(),
      category: form.category.trim(),
      phone: form.phone.trim(),
      lang,
      submittedAt: new Date().toISOString(),
    });

    try {
      await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body,
        redirect: "follow",
        mode: "no-cors", // Apps Script returns 302 → opaque OK
      });
    } catch (_) {
      /* Even if the response is opaque/blocked, the request still leaves
       * the browser. We treat this path as successful per spec. */
    }

    setSuccess(true);
    setSubmitting(false);

    /* Auto-dismiss exactly 3 s after the success message appears */
    setTimeout(() => { onClose(); }, 3000);
  }, [form, lang, submitting, success, onClose]);

  if (!open) return null;

  return (
    <div
      className={"karch-modal fixed inset-0 z-[100] flex items-center justify-center bg-[#0A1520]/80 backdrop-blur-xl" + (open ? " is-open" : "") + (success ? " is-done" : "")}
      role="dialog"
      aria-modal="true"
      aria-labelledby="karch-title"
      data-testid="architecture-modal"
    >
      <div className="karch-modal__scrim" onClick={() => !submitting && onClose()} />

      <div className="karch-modal__panel relative w-full max-w-lg bg-white/5 border border-white/10 rounded-2xl p-8 shadow-[0_0_40px_rgba(0,255,255,0.1)]">
        <div className="karch-modal__corners" aria-hidden="true">
          <span /><span /><span /><span />
        </div>

        <button
          type="button"
          className="karch-modal__close"
          onClick={() => !submitting && onClose()}
          aria-label={t("arch.close")}
          data-testid="architecture-close"
        >
          <svg viewBox="0 0 20 20" width="14" height="14"><path d="M5 5 L15 15 M15 5 L5 15" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
        </button>

        <header className="karch-modal__head">
          <span className="karch-modal__tag">{t("arch.tag")}</span>
          <h2 id="karch-title" className="karch-modal__title">{t("arch.title")}</h2>
          <p className="karch-modal__sub">{t("arch.sub")}</p>
        </header>

        <div className={"karch-modal__body" + (success ? " is-hidden" : "")}>
          <form className="karch-form" onSubmit={submit} noValidate>
            <div className="karch-field">
              <label htmlFor="karch-name">{t("arch.nameLabel")}</label>
              <input
                id="karch-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder={t("arch.namePh")}
                data-testid="architecture-name"
                disabled={submitting}
                autoComplete="off"
              />
              {err.name && <span className="karch-field__err">{err.name}</span>}
            </div>

            <div className="karch-field">
              <label htmlFor="karch-company">{t("arch.companyLabel")}</label>
              <input
                id="karch-company"
                type="text"
                value={form.company}
                onChange={(e) => setForm((p) => ({ ...p, company: e.target.value }))}
                placeholder={t("arch.companyPh")}
                data-testid="architecture-company"
                disabled={submitting}
                autoComplete="off"
              />
              {err.company && <span className="karch-field__err">{err.company}</span>}
            </div>

            <div className="karch-field">
              <label htmlFor="karch-email">{t("arch.emailLabel")}</label>
              <input
                id="karch-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder={t("arch.emailPh")}
                data-testid="architecture-email"
                disabled={submitting}
                autoComplete="off"
              />
              {err.email && <span className="karch-field__err">{err.email}</span>}
            </div>

            <div className="karch-field karch-field--select">
              <label htmlFor="karch-category">{t("arch.categoryLabel")}</label>
              <div className="karch-select">
                <select
                  id="karch-category"
                  value={form.category}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                  data-testid="architecture-category"
                  disabled={submitting}
                >
                  <option value="" disabled>{t("arch.categoryPh")}</option>
                  <option value="voice">{t("arch.cat.voice")}</option>
                  <option value="logic">{t("arch.cat.logic")}</option>
                  <option value="sovereign">{t("arch.cat.sovereign")}</option>
                  <option value="other">{t("arch.cat.other")}</option>
                </select>
                <span className="karch-select__chev" aria-hidden="true">
                  <svg viewBox="0 0 12 12" width="10" height="10"><path d="M2 4 L6 8 L10 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
                </span>
              </div>
              {err.category && <span className="karch-field__err">{err.category}</span>}
            </div>

            <div className="karch-field karch-field--phone">
              <label htmlFor="karch-phone">{t("arch.phoneLabel")}</label>
              <input
                id="karch-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                placeholder={t("arch.phonePh")}
                data-testid="architecture-phone"
                disabled={submitting}
                autoComplete="off"
              />
              {err.phone && <span className="karch-field__err">{err.phone}</span>}
            </div>

            <div className="karch-form__actions">
              <label
                className="flex items-start gap-3 mb-5 cursor-pointer text-sm text-white/70 leading-snug select-none"
                data-testid="architecture-gdpr-label"
              >
                <span className="relative inline-flex mt-0.5 shrink-0">
                  <input
                    type="checkbox"
                    required
                    checked={gdprAccepted}
                    onChange={(e) => setGdprAccepted(e.target.checked)}
                    disabled={submitting}
                    data-testid="architecture-gdpr"
                    className="w-5 h-5 shrink-0 appearance-none rounded-[4px] bg-white/[0.04] border border-white/25 backdrop-blur-md checked:bg-[var(--il-cyan)] checked:border-[var(--il-cyan)] checked:shadow-[0_0_12px_var(--il-cyan)] hover:border-[var(--il-cyan)]/60 focus:outline-none focus:ring-2 focus:ring-[var(--il-cyan)]/50 transition-all duration-300 cursor-pointer"
                  />
                  <svg
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 m-auto w-4 h-4 transition-opacity duration-200"
                    style={{ opacity: gdprAccepted ? 1 : 0 }}
                  >
                    <path
                      d="M4 10.5 L8.2 14.6 L16 6.4"
                      fill="none"
                      stroke="#FFFFFF"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span>
                  {lang === "de"
                    ? "Ich stimme zu, dass meine Angaben zur Kontaktaufnahme gespeichert werden. "
                    : "I agree that my data may be processed to answer my request. "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="architecture-gdpr-link"
                    className="text-[var(--il-cyan)] underline underline-offset-2 hover:opacity-80 transition-opacity"
                  >
                    ({lang === "de" ? "Datenschutzerklärung" : "Privacy Policy"})
                  </a>
                </span>
              </label>

              <button
                type="submit"
                className={"karch-cta whitespace-nowrap !min-w-fit !w-auto !px-8" + (submitting ? " is-submitting" : "") + (!gdprAccepted ? " is-locked" : "")}
                disabled={submitting || !gdprAccepted}
                aria-disabled={submitting || !gdprAccepted}
                data-testid="architecture-submit"
                style={!gdprAccepted && !submitting ? { opacity: 0.4, cursor: "not-allowed", boxShadow: "none" } : undefined}
              >
                <span className="karch-cta__label whitespace-nowrap">
                  {submitting ? t("arch.submitting") : t("arch.submit")}
                </span>
                <span className="karch-cta__glow" aria-hidden="true" />
              </button>
            </div>
          </form>
        </div>

        <div
          className={"karch-modal__success" + (success ? " is-on" : "")}
          aria-hidden={!success}
          data-testid="architecture-success"
        >
          <span className="karch-modal__successIcon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M5 12 L10 17 L19 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <p className="karch-modal__successMsg">{t("arch.success")}</p>
        </div>
      </div>
    </div>
  );
};

export default ArchitectureModal;
